import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import queryString from 'query-string';
// mantine
import { Drawer } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
// components
import PageContainer from '@/components/Layout/components/PageContainer';
import Header from '@/components/Header';
import Filters from '@/pages/History/components/Filters';
import Messages from '@/pages/History/components/Messages';
// utils
import { getActs } from '@/pages/History/History.utils.ts';
import {
    useActsPaginatedQuery,
    usePersonaQuery
} from '@/services/bff/bff.queries.ts';
import type { THistoryFilters } from '@/pages/History/History.types.ts';

const initialFilters: THistoryFilters = {
    query: ''
};

const DEBOUNCE_TIMEOUT = 200;
const DEFAULT_PAGES_NUMBER = 0;
const DEFAULT_TOTAL = 0;

const History = () => {
    const [searchParams] = useSearchParams();
    const [isOpened, handlers] = useDisclosure();
    const [filters, setFilters] = useState<THistoryFilters>(initialFilters);
    const [debounced] = useDebouncedValue(filters, DEBOUNCE_TIMEOUT);

    const { data: contact } = usePersonaQuery(
        searchParams.get('user_persona_id') ?? undefined
    );

    const { data, hasNextPage, fetchNextPage, isFetching } =
        useActsPaginatedQuery({
            per: 50,
            page: 1,
            turn_type: 'custom',
            event_idns: [
                'user_message',
                'agent_message',
                'session_started',
                'session_ended'
            ],
            session_id: searchParams.get('session_id') ?? undefined,
            user_persona_id: searchParams.get('user_persona_id') ?? undefined,
            ...debounced
        });

    useEffect(() => {
        const parsed = queryString.parse(searchParams.toString(), {
            arrayFormat: 'bracket'
        });
        const connectors = (parsed.connectors as string[] | undefined) ?? [];
        setFilters((prevState) => ({
            ...prevState,
            connectors
        }));
    }, [searchParams]);

    const acts = useMemo(() => getActs(data?.pages), [data?.pages]);

    const total = data?.pages[0]?.metadata.total ?? DEFAULT_TOTAL;
    const pagesNumber = data?.pages.length ?? DEFAULT_PAGES_NUMBER;

    const handleDrawerClose = () => {
        handlers.close();
    };

    return (
        <>
            <Helmet>
                <title>NEWO - History</title>
            </Helmet>
            <Header title={contact?.name} />
            <PageContainer>
                <Messages
                    fetchNextPage={fetchNextPage}
                    hasNextPage={hasNextPage}
                    isFetching={isFetching}
                    items={acts}
                    pagesNumber={pagesNumber}
                />
            </PageContainer>
            <Drawer
                opened={isOpened}
                position="right"
                title="Filters"
                onClose={handleDrawerClose}
            >
                <Filters total={total} />
            </Drawer>
        </>
    );
};

export default History;
